import { Button, Section } from "@vild/site-ui";
import { Activity, Shield, Terminal } from "lucide-react";
import React from "react";
import { HeroAnimation } from "../components/HeroAnimation";

const BentoCard = ({
  children,
  className = "",
  title,
  subtitle,
  icon: Icon,
}: any) => (
  <div
    className={`relative overflow-hidden rounded-2xl bg-zinc-900/80 backdrop-blur-sm border border-zinc-800/50 p-6 flex flex-col justify-between group hover:border-zinc-600/60 hover:shadow-2xl hover:shadow-violet-500/10 transition-all duration-500 hover:-translate-y-1 ${className}`}
  >
    <div className="z-10 relative">
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center mb-4 text-zinc-300 group-hover:text-white group-hover:from-violet-600/20 group-hover:to-violet-500/20 transition-all duration-300 shadow-lg">
        <Icon className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
      </div>
      <h3 className="text-xl font-bold text-white mb-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-violet-200 group-hover:to-violet-300 transition-all duration-300">{title}</h3>
      <p className="text-sm text-zinc-400 group-hover:text-zinc-300 transition-colors duration-300">{subtitle}</p>
    </div>
    <div className="mt-6 relative z-10">{children}</div>

    {/* Enhanced Background Effects */}
    <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 via-transparent to-violet-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-violet-500/10 to-transparent rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
    <div className="absolute -inset-px bg-gradient-to-r from-violet-500/20 via-transparent to-violet-400/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
  </div>
);

export const Home: React.FC = () => {
  return (
    <>
      {/* HERO SECTION */}
      <div className="relative pt-16 pb-20 overflow-hidden">
        {/* Abstract Background */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-gradient-to-r from-violet-600/15 via-violet-500/10 to-violet-400/15 rounded-full blur-[100px] -z-10 opacity-50 pointer-events-none"></div>

        {/* Floating Geometric Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 border border-violet-400/20 rounded-lg rotate-12 animate-bounce -z-10" style={{animationDuration: '6s'}}></div>
        <div className="absolute top-40 right-20 w-16 h-16 bg-gradient-to-br from-violet-500/10 to-violet-400/10 rounded-full blur-sm animate-pulse -z-10" style={{animationDuration: '8s'}}></div>
        <div className="absolute bottom-40 left-1/4 w-12 h-12 border border-violet-400/30 rounded-full animate-bounce -z-10" style={{animationDuration: '7s', animationDelay: '1s'}}></div>
        <div className="absolute bottom-20 right-1/3 w-24 h-24 bg-gradient-to-tl from-violet-500/5 to-violet-400/5 rounded-lg rotate-45 animate-pulse -z-10" style={{animationDuration: '5s'}}></div>

        <Section className="text-center pt-0 pb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-900 border border-zinc-800 text-xs font-medium text-zinc-400 mb-8 hover:border-zinc-700 transition-colors cursor-default">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--site-accent-400)] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[var(--site-accent-500)]"></span>
            </span>
            v1 now available, read about it <a href="/docs/changelog" className="text-[var(--site-accent-400)] hover:text-[var(--site-accent-300)] transition-colors">here</a>.
          </div>

          <h1 className="text-5xl md:text-8xl font-bold tracking-tight text-white mb-8">
            Transform media,
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-zinc-200 via-violet-300 to-violet-400 animate-pulse">
              automatically.
            </span>
          </h1>

          <p className="text-xl text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            The production runtime for pipelines. Build, scale, and monitor{" "}
            <strong>AI-powered media workflows</strong> without managing infrastructure.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-20">
            <Button
              size="lg"
              variant="inverse"
              asLink="/signup"
              className="bg-gradient-to-r from-violet-600 via-violet-500 to-violet-400 hover:from-violet-500 hover:via-violet-400 hover:to-violet-300 shadow-xl shadow-violet-500/25 hover:shadow-2xl hover:shadow-violet-500/30 transition-all duration-300 hover:scale-105"
            >
              Start free — $10 credits
            </Button>
            <Button
              size="lg"
              variant="outline"
              asLink="/docs"
              className="border-zinc-600 hover:border-zinc-400 hover:bg-zinc-800/50 backdrop-blur-sm transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-zinc-500/10"
            >
              Read the documentation
            </Button>
          </div>

          {/* HERO VISUAL: Pipeline Simulator (REMOTION) */}
          <div className="max-w-5xl mx-auto">
            <div className="h-[300px] md:h-[500px] relative">
              <HeroAnimation />
            </div>
            <div className="text-center mt-4 font-mono text-xs text-zinc-600">
              Fig 1. Automated asset processing pipeline (Rendered with Remotion)
            </div>
          </div>
        </Section>
      </div>

      {/* SOCIAL PROOF */}
      <div className="border-y border-zinc-900/50 bg-gradient-to-r from-zinc-950 via-zinc-900/20 to-zinc-950 py-12 overflow-hidden relative">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-zinc-800/5 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-6 relative">
          <p className="text-center text-sm font-mono text-zinc-500 mb-8 tracking-widest">POWERS PRODUCTION AT</p>
          <div className="flex flex-wrap justify-center items-center gap-12 md:gap-20">
            {["ACME Studios", "FrameFlow", "StreamWorks", "PixelPerfect", "RenderCo"].map((name, index) => (
              <div
                key={name}
                className="text-xl font-black tracking-tighter text-zinc-400 hover:text-white font-sans transition-all duration-300 hover:scale-110 opacity-60 hover:opacity-100 cursor-default"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {name}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* STATS SECTION */}
      <div className="py-16 border-b border-zinc-900/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="group">
              <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-violet-300 mb-2 animate-pulse">
                10K+
              </div>
              <div className="text-sm text-zinc-400 uppercase tracking-wide">Pipelines Created</div>
            </div>
            <div className="group">
              <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-300 to-violet-400 mb-2 animate-pulse" style={{animationDelay: '0.2s'}}>
                500K+
              </div>
              <div className="text-sm text-zinc-400 uppercase tracking-wide">Media Files Processed</div>
            </div>
            <div className="group">
              <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-violet-500 mb-2 animate-pulse" style={{animationDelay: '0.4s'}}>
                99.9%
              </div>
              <div className="text-sm text-zinc-400 uppercase tracking-wide">Uptime SLA</div>
            </div>
            <div className="group">
              <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-500 to-violet-400 mb-2 animate-pulse" style={{animationDelay: '0.6s'}}>
                24/7
              </div>
              <div className="text-sm text-zinc-400 uppercase tracking-wide">Runtime Support</div>
            </div>
          </div>
        </div>
      </div>

      {/* BENTO GRID FEATURES */}
      <Section>
        <div className="mb-12 animate-fade-in-up">
          <h2 className="text-3xl md:text-5xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-white via-zinc-200 to-zinc-400">The production runtime.</h2>
          <p className="text-xl text-zinc-400 max-w-2xl">Everything you need to run media pipelines at scale.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 grid-rows-2 gap-4 h-auto md:h-[600px]">
          {/* Runtime: Large Card */}
          <BentoCard className="md:col-span-2 md:row-span-2" title="Serverless Runtime" subtitle="Auto-scaling Media & AI workers." icon={Terminal}>
            <div className="mt-8 h-full bg-zinc-950 rounded-lg border border-zinc-800 p-4 font-mono text-xs text-zinc-300 relative overflow-hidden">
              <div className="absolute top-4 right-4 text-zinc-700 text-[10px]">LIVE LOGS</div>
              <div className="space-y-2 opacity-70">
                <div>&gt; initialized worker-pool (us-east-1)</div>
                <div className="text-zinc-500"> — capacity: 50 nodes</div>
                <div className="text-zinc-500"> — gpu: enabled (T4)</div>
                <div className="text-green-400">&gt; processing job #8291...</div>
                <div className="pl-4 border-l border-zinc-800 text-zinc-500">render -i input.mov -c:v libx264...</div>
                <div className="text-blue-400">&gt; uploading library items (4 files)</div>
                <div className="animate-pulse">_</div>
              </div>
            </div>
          </BentoCard>

          {/* Gates */}
          <BentoCard title="Approval Gates" subtitle="Human-in-the-loop review." icon={Shield}>
            <div className="mt-4 flex items-center justify-between bg-zinc-800/50 p-3 rounded border border-zinc-800">
              <div className="text-xs font-medium text-zinc-300">Auto-Approve</div>
              <div className="w-8 h-4 bg-zinc-700 rounded-full relative">
                <div className="absolute top-0.5 left-0.5 w-3 h-3 bg-zinc-500 rounded-full"></div>
              </div>
            </div>
          </BentoCard>

          {/* Observability */}
          <BentoCard title="Observability" subtitle="Trace every frame." icon={Activity}>
            <div className="mt-4 flex items-end gap-1 h-12 w-full px-2">
              {[20, 45, 30, 80, 50, 90, 40].map((h, i) => (
                <div key={i} className="flex-1 bg-[var(--site-accent-500)]/20 rounded-t hover:bg-[var(--site-accent-500)] transition-colors" style={{ height: `${h}%` }}></div>
              ))}
            </div>
          </BentoCard>
        </div>
      </Section>

      {/* CTA SECTION */}
      <Section className="py-24">
        <div className="relative rounded-3xl bg-gradient-to-br from-zinc-900 via-zinc-800/50 to-zinc-900 border border-zinc-700/50 p-12 md:p-20 text-center overflow-hidden shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-r from-violet-500/10 via-violet-400/10 to-violet-500/10"></div>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-gradient-to-r from-violet-500/20 via-violet-400/20 to-violet-500/20 rounded-full blur-3xl -z-10"></div>
          <div className="absolute -inset-1 bg-gradient-to-r from-violet-500/20 via-violet-400/20 to-violet-500/20 rounded-3xl opacity-20"></div>
          <div className="relative z-10">
            <h2 className="text-4xl md:text-6xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-white via-violet-200 to-violet-300">
              Ready to transform?
            </h2>
            <p className="text-xl text-zinc-300 mb-10 max-w-2xl mx-auto leading-relaxed">
              Join thousands of developers automating media workflows with Vild.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button
                size="lg"
                className="w-full sm:w-auto bg-gradient-to-r from-violet-600 via-violet-500 to-violet-400 hover:from-violet-500 hover:via-violet-400 hover:to-violet-300 shadow-xl shadow-violet-500/25 hover:shadow-2xl hover:shadow-violet-500/30 transition-all duration-300 hover:scale-105"
                asLink="/signup"
              >
                Start free — $10 credits
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-zinc-600 hover:border-zinc-400 hover:bg-zinc-800/50 backdrop-blur-sm transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-zinc-500/10"
                asLink="/starters"
              >
                Explore Starters
              </Button>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
};

